/**
 * 顧客リスト (営業ロール用)。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6.1, §11
 *
 * Phase A / P-C 拡張:
 *  - 各行に「最新商談ステータス」バッジを追加 (Deal.stage を 8 種類のラベルに変換)
 *  - 行クリックで `/sales/contacts/:contactId` に遷移
 *  - mail_contacts 物理コレクションを `promotionLevel IN ('customer', 'inactive')` で論理フィルタ
 */
import type {
  ContactKind,
  ContactPromotionLevel,
  Deal,
  DealStage,
  MailContact,
} from '@goenhub/shared';
import { Building2, ChevronRight, Search, UserCircle2, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useDeals } from '@/hooks/useDeals';
import { useMailContacts } from '@/hooks/useMailContacts';
import { paths } from '@/routes/paths';

import { DealStageBadge } from './components/DealStageBadge';

interface ContactWithMeta extends MailContact {
  id: string;
}

const KIND_LABEL: Record<ContactKind, string> = {
  btob: 'BtoB',
  btoc: 'BtoC',
  unknown: 'Unknown',
};

const PROMOTION_LABEL: Record<ContactPromotionLevel, string> = {
  lead: 'リード',
  customer: '顧客',
  inactive: '休眠',
};

/** Deal[] を contactId ごとにグルーピングし、各 contact の最新 (lastTouchedAt 降順の先頭) Deal を返す。 */
function buildLatestDealMap(
  deals: Array<Deal & { id: string }> | undefined,
): Map<string, { stage: DealStage; title: string; id: string }> {
  const map = new Map<string, { stage: DealStage; title: string; id: string }>();
  if (!deals) return map;
  for (const d of deals) {
    if (!map.has(d.contactId)) {
      // useDeals は lastTouchedAt desc で取得しているので最初のヒットが最新
      map.set(d.contactId, { stage: d.stage, title: d.title, id: d.id });
    }
  }
  return map;
}

export function SalesContactsPage() {
  const navigate = useNavigate();
  const { data: contacts, isLoading } = useMailContacts({ limit: 1000 });
  const { data: allDeals, isLoading: isLoadingDeals } = useDeals();
  const [keyword, setKeyword] = useState('');

  const latestDealByContact = useMemo(() => buildLatestDealMap(allDeals), [allDeals]);

  const filtered = useMemo(() => {
    if (!contacts) return [] as ContactWithMeta[];
    const k = keyword.trim().toLowerCase();
    return contacts.filter((c) => {
      const level = c.promotionLevel ?? 'lead';
      if (level !== 'customer' && level !== 'inactive') return false;
      if (!k) return true;
      const hay = `${c.name ?? ''} ${c.defaultEmail ?? ''} ${c.companyName ?? ''}`.toLowerCase();
      return hay.includes(k);
    }) as ContactWithMeta[];
  }, [contacts, keyword]);

  return (
    <Screen
      title="顧客リスト"
      description="営業段階 (customer / inactive) の顧客のみ表示。lead はマーケタブを参照。"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[var(--text-muted)]" />
          <Input
            type="text"
            placeholder="氏名・メール・会社名で検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {isLoading || isLoadingDeals ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={
            (contacts?.length ?? 0) === 0
              ? '顧客が登録されていません'
              : '営業段階の顧客がいません'
          }
          description="個別説明申込・見積依頼の送信があると lead → customer に自動昇格します (Phase 3+)。"
        />
      ) : (
        <div className="rounded-md border border-[var(--border)] overflow-hidden">
          <div className="grid grid-cols-[2fr_1.2fr_0.7fr_1fr_0.8fr_1fr_auto] gap-3 px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
            <div>氏名 / 会社</div>
            <div>メール</div>
            <div>タイプ</div>
            <div>最新商談</div>
            <div>状態</div>
            <div>初契約日</div>
            <div className="w-4"></div>
          </div>
          {filtered.map((c, i) => {
            const latest = latestDealByContact.get(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() =>
                  navigate(paths.salesContactDetail.replace(':contactId', c.id))
                }
                className={`group grid grid-cols-[2fr_1.2fr_0.7fr_1fr_0.8fr_1fr_auto] gap-3 px-4 py-2 items-center w-full text-left hover:bg-[var(--bg-hover)] transition-colors ${
                  i < filtered.length - 1 ? 'border-b border-[var(--border)]' : ''
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    {c.kind === 'btob' ? (
                      <Building2 className="size-3.5 text-[var(--accent)] flex-shrink-0" />
                    ) : (
                      <UserCircle2 className="size-3.5 text-[var(--accent-purple)] flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium truncate">{c.name}</span>
                  </div>
                  {c.companyName && (
                    <div className="text-xs text-[var(--text-muted)] truncate ml-5">
                      {c.companyName}
                      {c.department ? ` / ${c.department}` : ''}
                    </div>
                  )}
                </div>
                <div className="text-xs text-[var(--text-secondary)] truncate">
                  {c.defaultEmail}
                </div>
                <div>
                  {c.kind ? <Badge variant="default">{KIND_LABEL[c.kind]}</Badge> : '—'}
                </div>
                <div className="min-w-0">
                  <DealStageBadge stage={latest?.stage} />
                  {latest && (
                    <div className="text-[10px] text-[var(--text-muted)] truncate mt-0.5">
                      {latest.title}
                    </div>
                  )}
                </div>
                <div>
                  {(c.promotionLevel ?? 'lead') === 'inactive' ? (
                    <Badge variant="default">{PROMOTION_LABEL.inactive}</Badge>
                  ) : (
                    <Badge variant="approved">{PROMOTION_LABEL.customer}</Badge>
                  )}
                  {c.isTestContact === true && (
                    <Badge
                      variant="danger"
                      title="テスト顧客: 商談の自動マージ対象外"
                    >
                      テスト
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-[var(--text-muted)] tabular-nums">
                  {c.customerSince
                    ? typeof c.customerSince === 'string'
                      ? new Date(c.customerSince).toLocaleDateString('ja-JP')
                      : c.customerSince &&
                        typeof c.customerSince === 'object' &&
                        'toDate' in c.customerSince
                        ? (c.customerSince as { toDate: () => Date })
                            .toDate()
                            .toLocaleDateString('ja-JP')
                        : '—'
                    : '—'}
                </div>
                <ChevronRight className="size-4 text-[var(--text-muted)] group-hover:text-[var(--accent)] transition-colors" />
              </button>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
